import React, { Fragment } from 'react';
import { Popover, Transition } from '@headlessui/react';
import Drawer from 'components/commercetools-ui/atoms/drawer';
import MiniCart from 'components/commercetools-ui/organisms/miniCart';
import { useMiniCart } from 'context/miniCart';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop } from 'helpers/utils/screensizes';
import CartIcon from 'icons/cart';
import CloseIcon from 'icons/close';
import { useCart } from 'frontastic';

const CartButton = () => {
  const [isDesktop] = useMediaQuery(desktop);

  const { show: showMiniCart, hide: hideMiniCart, isOpen: isMiniCartOpen } = useMiniCart();

  const { data } = useCart();
  const lineItemCount = data?.lineItems?.reduce((prev, val) => (prev || 0) + (val.count || 0), 0) || 0;

  return !isDesktop ? (
    <div data-testid="mobile-header-cart-button">
      <div className="relative mr-2 flex flex-col items-center justify-center" onClick={() => showMiniCart()}>
        {!isDesktop && lineItemCount > 0 && (
          <div className="leading-0 absolute -right-[70%] -top-[70%] flex size-6 items-center justify-center rounded-full bg-accent-red text-sm text-white">
            <span className="sr-only">Cart Contains </span>
            {lineItemCount}
            <span className="sr-only"> Items</span>
          </div>
        )}
        <div className="mb-1 size-5">
          <CartIcon />
        </div>
        <span className="hidden text-nowrap text-center text-[12px] font-semibold lg:block">
          Cart ({lineItemCount})
        </span>
      </div>
      {isMiniCartOpen && (
        <CloseIcon className="fixed left-3 top-5 z-[700] size-4" color="white" aria-label="Close Mini Cart" />
      )}
      <div data-testid="mobile-cart-drawer">
        <Drawer isOpen={isMiniCartOpen} direction="right" className="w-[90vw] bg-white" onClose={() => hideMiniCart()}>
          <MiniCart />
        </Drawer>
      </div>
    </div>
  ) : (
    <Popover>
      {() => (
        <>
          <Popover.Button
            className={`${
              isMiniCartOpen ? 'border-gray-400 border-b-[#fcfcfa] bg-[#fcfcfa]' : 'border-transparent'
            } relative z-40 mb-px flex cursor-pointer items-center rounded-t-md border px-1 py-[9px] focus-visible:ring-2 focus-visible:ring-white/75`}
            aria-label={`Cart (${lineItemCount}) - Cart Button`}
            onKeyDown={(e) => {
              if (e.code === 'Enter') {
                isMiniCartOpen ? hideMiniCart() : showMiniCart();
              }
            }}
            onMouseDown={() => (isMiniCartOpen ? hideMiniCart() : showMiniCart())}
          >
            <div data-testid="desktop-header-cart-button" className="flex flex-col items-center justify-center">
              <div className="mb-1 size-5">
                <CartIcon />
              </div>
              <span className="hidden text-nowrap text-xs font-semibold lg:block">Cart ({lineItemCount})</span>
            </div>
          </Popover.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
            show={isMiniCartOpen}
          >
            <Popover.Panel className="absolute right-0 z-30 -mt-px origin-top-right rounded-md rounded-tr-none border border-gray-400">
              <section data-testid="desktop-cart-popup">
                <MiniCart />
              </section>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default CartButton;
